import { useMemo } from 'react';
import { getModuleZIndex, getModuleBBox, BODY_MODULE_TYPES } from '@/lib/furnitureData';

const BG_GRADIENT_ID = 'tpl-preview-bg';
const FLOOR_GRADIENT_ID = 'tpl-preview-floor';

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return { r: 196, g: 167, b: 108 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

function shade(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const adj = (c) => Math.max(0, Math.min(255, Math.round(c + amount)));
  const toHex = (c) => c.toString(16).padStart(2, '0');
  return `#${toHex(adj(r))}${toHex(adj(g))}${toHex(adj(b))}`;
}

function fillFor(mod) {
  const base = mod.color_hex || '#C4A76C';
  if (mod.material === 'glass') return 'rgba(180, 210, 220, 0.55)';
  if (mod.material === 'metal') return base;
  return base;
}

export default function TemplatePreview({ template, className = '' }) {
  const { viewBox, items, hasAny } = useMemo(() => {
    const modules = template?.modules || [];
    if (!modules.length) {
      return { viewBox: '0 0 400 300', items: [], hasAny: false };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    modules.forEach((m) => {
      const b = getModuleBBox(m);
      if (b.x1 < minX) minX = b.x1;
      if (b.y1 < minY) minY = b.y1;
      if (b.x2 > maxX) maxX = b.x2;
      if (b.y2 > maxY) maxY = b.y2;
    });

    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    const padX = w * 0.08;
    const padY = h * 0.1;
    const vbW = w + padX * 2;
    const vbH = h + padY * 2;

    const sorted = [...modules].sort(
      (a, b) => getModuleZIndex(a.type) - getModuleZIndex(b.type)
    );

    const items = sorted.map((m) => {
      const b = getModuleBBox(m);
      return {
        id: m.id,
        x: b.x1 - minX + padX,
        y: b.y1 - minY + padY,
        w: b.w,
        h: b.h,
        type: m.type,
        material: m.material,
        fill: fillFor(m),
        stroke: shade(m.color_hex || '#C4A76C', -40),
        isBody: BODY_MODULE_TYPES.includes(m.type),
      };
    });

    return {
      viewBox: `0 0 ${vbW} ${vbH}`,
      items,
      hasAny: true,
    };
  }, [template]);

  if (!hasAny) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-[#E8E5E1] ${className}`}>
        <span className="text-xs text-[#9A9A9A]">Нет модулей</span>
      </div>
    );
  }

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      className={`w-full h-full ${className}`}
      role="img"
      aria-label={template?.name_ru || 'Превью мебели'}
    >
      <defs>
        <linearGradient id={BG_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5F2EC" />
          <stop offset="100%" stopColor="#E8E3DB" />
        </linearGradient>
        <linearGradient id={FLOOR_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,0,0,0.08)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${BG_GRADIENT_ID})`} />

      {items.map((it) => {
        const opacity =
          it.material === 'glass' ? 0.55 :
          it.material ? 0.95 : 0.7;
        const strokeW = Math.max(0.4, Math.min(it.w, it.h) * 0.02);

        return (
          <g key={it.id}>
            <rect
              x={it.x}
              y={it.y}
              width={it.w}
              height={it.h}
              fill={it.fill}
              stroke={it.stroke}
              strokeWidth={strokeW}
              opacity={opacity}
              rx={it.isBody ? 0.6 : 0.3}
              ry={it.isBody ? 0.6 : 0.3}
            />
            {it.type === 'drawer' && it.w > 6 && (
              <line
                x1={it.x + it.w * 0.35}
                y1={it.y + it.h * 0.5}
                x2={it.x + it.w * 0.65}
                y2={it.y + it.h * 0.5}
                stroke={shade(it.fill, -60)}
                strokeWidth={strokeW * 1.5}
                strokeLinecap="round"
                opacity="0.7"
              />
            )}
            {it.type === 'door' && it.h > 10 && (
              <circle
                cx={it.x + it.w * 0.85}
                cy={it.y + it.h * 0.5}
                r={Math.max(0.4, Math.min(it.w, it.h) * 0.035)}
                fill={shade(it.fill, -80)}
                opacity="0.8"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
